import React from 'react';
import { injectIntl } from 'react-intl';
import { i18nIntl } from '../lib/ep.js';

import BuilderCanvas from '../components/builder/BuilderCanvas';
import BuilderSidebar from '../components/builder/BuilderSidebar';
import Checkbox from '../components/builder/Checkbox';
import MatrixSelector from '../components/builder/MatrixSelector';
import MediaUploader from '../components/builder/MediaUploader';
import Model from '../components/builder/Model';
import { NavigationTabsBuilder } from '../components/builder/NavigationTabs';
import RichEditor from '../components/builder/RichEditor';
import Select from '../components/builder/Select';
import TextArea from '../components/builder/TextArea.js';
import Tooltip from '../components/builder/Tooltip.js';

import ContainerSliders from '../components/backend/elements/ContainerSliders';

import Layout from '../components/front/Layout';

import PromotionStageMosaic from '../components/users/PromotionStageMosaic';

import transferPlaceholder from '../hoc/transferPlaceholder';
import BuilderMain from '../components/builder/BuilderMain.js';
import BuilderSubSectionTitle from '../components/backend/BuilderSubSectionTitle.js';
import { stage_promotion_types } from '../data/bootstrap_stages.js';
const RichPlaceHolder = injectIntl(transferPlaceholder(RichEditor));
const TextAreaPlaceHolder = injectIntl(transferPlaceholder(TextArea));

const StageManagerEdit = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const stages = props.stages;

	const section_main_path = props?.section_main_path ?? 'stages';

	const default_description_text = `<h1>${i18n("Participation stages")}</h1><p>${i18n("Click on the stage you want to participate in. The stage must be open.")}</p>`;

	const mosaic_layout = props?.pm?.config?.layout?.stage_mosaic ?? { layout_width:70, layout_num_cols:1, layout_cols_prop:40 };

	const gallery_stages = stages.filter((stage) => {
		return ['8'].includes(stage.Stage.type);
	});

	const img_proportions = [{
		value:'squared',
		icon:'knk-picture-square',
		tooltip:"Square (1:1)",
	}, {
		value:'landscape',
		icon:'knk-picture-landscape',
		tooltip:"Landscape (120:63)",
	}, {
		value:'portrait',
		icon:'knk-picture-portrait',
		tooltip:"Portrait (2:3)",
	}];

	const layouts = [{
		k:'mosaic',
		v:`${i18n("Mosaic")} (${i18n("default")})`,
	}, {
		k:'list',
		v:i18n("List"),
	}];

	const open_types = [{
		k:'none',
		v:`${i18n("***None")} (${i18n("default")})`,
	}, {
		k:'window',
		v:i18n("Window / Door effect"),
	}, {
		k:'zoom_in',
		v:i18n("Zoom-in effect"),
	}];

	const list_graphic_displays = [{
		k:'icon',
		v:`${i18n("Icon")} (${i18n("default")})`,
	}, {
		k:'image',
		v:i18n("Image"),
	}, {
		k:'none',
		v:i18n("***None"),
	}]

	const is_multistage = props?.pm?.is_multistage ?? false;
	const is_wl = ((props?.pm?.promo_type ?? '1') == '2') ? true : false;
	const display_advance = (is_multistage && is_wl) ? true : false;

	const default_script_placeholder = '<script type="text/javascript">\n\tdocument.addEventListener("DOMContentLoaded", function() {\n\t\t/* '+i18n("Your code goes here")+'... */\n\t});\n</script>';

	return (
		<BuilderMain pm={props?._promoModel || props.promoModel}>
			<BuilderSidebar onSave={props.savePromo}>
				<NavigationTabsBuilder
					active={`/${section_main_path}/manager/1`}
					sections={props.sections}
				/>

				<article>
					<Model model={props.pm} change={props.promoChange}>
						<MediaUploader
							auto_url={`/promotions/auto_img/${props.pm.id}`}
							auto_type="main"
							_class={{first:true}}
							imageField="mosaic_image"
							videoField="mosaic_video"
							full_name="full_mosaic_image"
							image={props.pm.full_mosaic_image}
							video={props.pm.mosaic_video}
						>Upload media</MediaUploader>

						<RichPlaceHolder
							placeholder={default_description_text}
							name="stages_mosaic_description"
							has_emojis={true}
						>Introduction message</RichPlaceHolder>

						{(stages.length >= 10) && (
							<Checkbox
								name="stages_mosaic_show_search_bar"
							>Show search bar</Checkbox>
						)}
					</Model>

					<div class="gui_not_in_top">
						<ContainerSliders
							section="stage_mosaic"
							layout={mosaic_layout}
							change={props.promoChange}
							display_only_width_slider={true}
						/>
					</div>
				</article>

				{(props.pm.promotion_type_id != stage_promotion_types.BRACKETS.id && is_wl) && (
					<article class="gui_not_in_top_huge">
						<BuilderSubSectionTitle
							title="Options"
							icon='knk-settings'
						/>

						<Model model={props.pm} change={props.promoChange}>
							<Select
								name="stages_mosaic_layout"
								values={layouts}
								_class={{ size:60 }}
							>Layout</Select>

							{(props.pm.stages_mosaic_layout == 'list') && (
								<Select
									_class={{ size:60 }}
									name="stages_mosaic_list_graphic_display"
									values={list_graphic_displays}
								>Graphic mode</Select>
							)}

							{(props.pm.stages_mosaic_layout != 'list') && (
								<MatrixSelector
									_class={{size:50}}
									name="stages_mosaic_image_proportion"
									_default_value='landscape'
									items={img_proportions}
									allow="custom"
								>Image proportion</MatrixSelector>
							)}

							{(props.pm.stages_mosaic_layout != 'list') && (
								<Select
									name="stages_mosaic_open_animation"
									values={open_types}
								>Effect when clicking on the stage image</Select>
							)}

							{(props.pm.stages_mosaic_open_animation == 'window') && (
								<MatrixSelector
									_class={{size:50}}
									name="stages_mosaic_open_animation_anchor"
									_default_value='left'
									value={props.pm?.stages_mosaic_open_animation_anchor ?? 'left'}
									only_center_edges={true}
									allow="xy"
								>Animation anchor</MatrixSelector>
							)}

							{(props.pm.stages_mosaic_layout != 'list') && (
								<Checkbox
									name="stages_mosaic_hide_name"
								>Hide stage name</Checkbox>
							)}

							<Checkbox
								name="stages_mosaic_hide_status"
							>Hide stage status</Checkbox>

							<Checkbox
								name="stages_mosaic_hide_completion_status"
								value={props.pm?.stages_mosaic_hide_completion_status ?? true}
							>Hide completion status</Checkbox>

							<Checkbox
								name="stages_mosaic_hide_expired_stages"
							>Hide expired stages</Checkbox>

							<Checkbox
								name="stages_mosaic_hide_future_stages"
							>Hide future stages</Checkbox>
							
							{(gallery_stages.length > 0) && (
								<Checkbox
									name="stages_mosaic_hide_voting_galleries"
								>Hide voting gallery stages</Checkbox>
							)}
							
							{(props.pm.is_multistage) && (
								<Checkbox
									name="stages_mosaic_hide_stage_participations"
								>Hide user participations</Checkbox>
							)}

							{(props.pm.stages_mosaic_layout != 'list') && (
								<Checkbox
									name="stages_mosaic_hide_stage_border"
								>Hide stage border</Checkbox>
							)}

							<Checkbox
								name="skip_redirect_one_stage"
							>Do not force automatic redirection to a stage if it is the only one visible</Checkbox>
						</Model>
					</article>
				)}

				{(display_advance) && (
					<article class="gui_not_in_top_huge">
						<BuilderSubSectionTitle
							title="Advanced"
							icon='knk-script'
						/>

						<Model model={props.pm} change={props.promoChange}>
							<TextAreaPlaceHolder
								name="stages_mosaic_code_tracking"
								_class={{ v_size:'huge' }}
								zoomable={true}
								is_monospaced={true}
								placeholder={default_script_placeholder}
								label_props={{
									tooltip:<Tooltip>In this field you can insert HTML, Javascript or any other code that can be read by a web browser.</Tooltip>,
								}}
							>Javascript, HTML and your own code</TextAreaPlaceHolder>
						</Model>
					</article>
				)}
			</BuilderSidebar>

			<BuilderCanvas
				is_adaptable={true}
				fit_to_window="content"
				enable_actions={true}
			>
				<Layout
					promo={props.pm}
					menus={props.promoModel.Custom__Menus}
					is_builder={true}
					page="stage_mosaic"
				>
					<PromotionStageMosaic
						stages={stages.filter((st)=>{return st.Stage.promotion_type_id != stage_promotion_types.MEDIA_AUTOCONTENT.id})}
						promo={props.pm}
						is_builder={true}
					/>
				</Layout>
			</BuilderCanvas>
		</BuilderMain>
	);
}

export default injectIntl(StageManagerEdit);